import type { SponsorCampaign } from "../campaignTypes";
import {
  CONTENT_IMAGE_URLS,
  HERO_IMAGE_URL,
  NEIGHBORHOOD_CLASSICS_IMAGE_URL,
  TIMES_SQUARE_IMAGE_URL,
  VENUE_IMAGE_URLS,
  type FoodTourConfig,
} from "./food-tour";

/**
 * HexClad-branded Food Tour ABM microsite.
 * Same layout as food-tour, seven nights (no Dos Caminos / Apollo Bagels).
 */
export const hexcladFoodTourConfig: FoodTourConfig = {
  brandName: "HexClad",
  brandLogoUrl: "",
  accentColor: "#E8672D",
  partnershipThesis:
    "HexClad belongs in the kitchens that keep New York running, next to the owners who built those rooms over decades.",
  brandWhyBody: [
    "This tour is built around the people behind seven New York food businesses. Owners, operators, and the teams that make each room work.",
    "A brand known for cookware that lasts fits nights that put those same businesses on stage, with comedy, food, and culture in the same frame.",
    "Seven sold-out rooms, seven owner stories, and a two-week cultural arc create a human New York setting that no banner buy can invent.",
  ],
  brandActivations: {
    onSiteEyebrow: "In the kitchen, with the audience, and to take home",
    contentEyebrow: "Connecting the HexClad brand online with each show",
    onSite: [
      "A venue-specific HexClad product featured at every show",
      "Cookware incorporated into kitchen and serving moments",
      '"Knives for the Killers" gifting in the comedians\' green room',
      "HexClad containers for guests and talent to take home leftovers",
      "Product displays, giveaways, and branded touchpoints throughout each venue",
      "Collaborative aprons to sell to guests featuring HexClad x ComedyUO branding",
    ],
    content: [
      "Hero video featuring David and Ethan attempting to cook the most popular dish from each restaurant featuring HexClad products",
      '"Built to Last" interviews spotlighting the small business owners and their stories of being in business for decades',
      "Behind-the-scenes kitchen content with owners walking through their iconic dishes",
      "A tour recap connecting seven restaurants through food, comedy, and New York culture",
    ],
    guestAccess: [],
  },
  howBrandTitle: "How we see HexClad showing up.",
  weekCopy: {
    neighborhood: {
      title: "Neighborhood Classics",
      body: "Multigenerational institutions like Zabar's, Barney Greengrass, Veselka, Russ & Daughters, and Economy Candy. The beating heart of NYC, after hours.",
    },
    timesSquare: {
      title: "Times Square Icons",
      body: "Margaritaville and Applebee's. Locals, visitors, and household names in rooms no traditional comedy club could invent.",
    },
  },
  tourSectionTitle: "Seven nights across New York.",
  tourSectionIntro:
    "Neighborhood classics Monday through Friday, then two Times Square icons the following week. Each night has its own comics, its own room, and its own owner story.",
  nightFlow: {
    title: "A typical night.",
    intro: "Same spine every stop. Different room, different comics, same sold-out energy.",
    steps: [
      {
        time: "6:30",
        title: "Doors",
        body: "Guests roll in. Drinks and house bites from the restaurant. The room fills fast.",
      },
      {
        time: "7:15",
        title: "Settle in",
        body: "Owner energy in the room. People grab seats. Partner presence lands before the first joke.",
      },
      {
        time: "7:30",
        title: "The show",
        body: "Host plus a surprise lineup. Intimate, sold out, and not a club night.",
      },
      {
        time: "9:00",
        title: "The after",
        body: "Guests linger. Photos, leftovers, sidewalk spillover. The night keeps talking.",
      },
    ],
  },
  heroFacts: [
    { label: "Venues", value: "7" },
    { label: "Guests", value: "850" },
    { label: "Comedians", value: "25+" },
    { label: "Weeks", value: "2" },
  ],
  sponsorshipTiers: {
    weekOneDates: ["1/11", "1/12", "1/13", "1/14", "1/15"],
    weekTwoDates: ["1/20", "1/21"],
    fullTour: {
      supporterPrice: "$12,000",
      sponsorPrice: "$24,000",
      supporterGets: [
        "Product featured in each venue",
        "Your logo included on the promotional flyer for the tour",
        "5 dedicated story posts on Instagram",
        "Logo included in email blast to a 38k+ waitlist",
        "10 tickets to the tour",
        "Reserved seating for your team",
        "Logo featured in the recap video",
      ],
      sponsorGets: [
        "Promo video with product feature",
        "Product feature in the green room",
        "Product tie-ins with the kitchens and serving",
        "Branded moment with packaging leftovers",
        "Opportunity to distribute giveaways",
        "Your branding featured in all promotional materials",
        "Dedicated shout-out at every show",
        "Dedicated grid post on Instagram and TikTok",
        "10 dedicated story posts on Instagram",
        "Feature on email blast to a 38k+ waitlist",
        "Organic inclusion on influencers' posts",
        "Exclusivity of category (no competing brands)",
        "Discounted sponsor fee for a future show",
        "20 tickets to the tour",
        "Reserved seating for your team",
        "Feature in the recap video",
      ],
      youProvide: [
        "Products for use in on-site and social media giveaways",
        "Point-of-connection promotional takeover materials",
        "Re-post tagged stories about the festival on Instagram (subject to team approval)",
      ],
    },
    weekOneOnly: {
      label: "Or just the 1st week (Neighborhood Classics)",
      supporterPrice: "$7,500",
      sponsorPrice: "$15,000",
    },
  },
  tourTitle: "The 2027 Big/Small Business Food Tour",
  timingLabel: "January 2027 · Prepared for HexClad",
  positioning:
    "Seven iconic New York restaurants. Intimate sold-out comedy. HexClad in the kitchen with the owners behind the rooms.",
  venues: [
    {
      id: "veselka",
      name: "Veselka",
      cuisine: "Ukrainian cuisine",
      week: "neighborhood",
      dateLabel: "Jan 11",
      capacity: 90,
      imageUrl: VENUE_IMAGE_URLS.veselka,
      note: "A multigenerational East Village institution after hours.",
    },
    {
      id: "barney-greengrass",
      name: "Barney Greengrass",
      cuisine: "American Jewish deli",
      week: "neighborhood",
      dateLabel: "Jan 12",
      capacity: 40,
      imageUrl: VENUE_IMAGE_URLS.barneyGreengrass,
      note: "An intimate Upper West Side classic with room for only a few dozen.",
    },
    {
      id: "russ-daughters",
      name: "Russ & Daughters",
      cuisine: "Bagel specialties",
      week: "neighborhood",
      dateLabel: "Jan 13",
      capacity: 180,
      imageUrl: VENUE_IMAGE_URLS.russDaughters,
      note: "Appetizing counter energy turned into a comedy room.",
    },
    {
      id: "zabars",
      name: "Zabar's",
      cuisine: "American Jewish grocer",
      week: "neighborhood",
      dateLabel: "Jan 14",
      capacity: 110,
      imageUrl: VENUE_IMAGE_URLS.zabars,
      note: "A neighborhood landmark New York already treats like theater.",
    },
    {
      id: "economy-candy",
      name: "Economy Candy",
      cuisine: "Candy and dessert",
      week: "neighborhood",
      dateLabel: "Jan 15",
      capacity: 110,
      imageUrl: VENUE_IMAGE_URLS.economyCandy,
      note: "Sweet, packed, and pure Lower East Side.",
    },
    {
      id: "applebees",
      name: "Applebee's",
      cuisine: "Times Square favorite",
      week: "timesSquare",
      dateLabel: "Jan 20",
      capacity: 120,
      imageUrl: VENUE_IMAGE_URLS.applebees,
      note: "I mean, c'mon now.",
    },
    {
      id: "margaritaville",
      name: "Margaritaville",
      cuisine: "Times Square favorite",
      week: "timesSquare",
      dateLabel: "Jan 21",
      capacity: 200,
      imageUrl: VENUE_IMAGE_URLS.margaritaville,
      note: "The one and only.",
    },
  ],
  stats: [
    {
      label: "Tour scale",
      value: "7 venues",
      detail: "About 850 guests across two weeks in January 2027",
    },
    {
      label: "Talent",
      value: "25+ comedians",
      detail: "A distinct lineup each night so the tour never repeats itself",
    },
    {
      label: "ComedyUO demand",
      value: "38,000",
      detail: "People on the waitlist for first access to tickets",
    },
    {
      label: "Venue reach",
      value: "1.1M+",
      detail: "Combined followers across the seven partner businesses",
    },
    {
      label: "Track record",
      value: "350+ shows",
      detail: "More than 250 comics and well over $200,000 earned for small businesses",
    },
  ],
  cta: {
    headline: "Let's build the HexClad activation together.",
    body: "",
    email: "mike@comedyuo.com",
    name: "",
    role: "",
  },
  images: {
    hero: HERO_IMAGE_URL,
    neighborhood: NEIGHBORHOOD_CLASSICS_IMAGE_URL,
    timesSquare: TIMES_SQUARE_IMAGE_URL,
    content: CONTENT_IMAGE_URLS,
  },
};

export const hexcladFoodTourCampaign: SponsorCampaign = {
  campaignSlug: "hexclad-food-tour",
  sponsorName: "HexClad",
  sponsorLogo: "/images/partners/food-tour/brand-fallback.svg",
  sponsorWordmark: "HexClad",
  comedyuoLogo: "https://storage.googleapis.com/cuo-website/comedyuo-logo-white.png",
  accentColor: hexcladFoodTourConfig.accentColor,
  eyebrow: "Prepared for HexClad · Food Tour partnership concept",
  campaignName: hexcladFoodTourConfig.tourTitle,
  shareTitle: "HexClad / ComedyUO Food Tour",
  shareImage: hexcladFoodTourConfig.images.hero || "/images/og-default.jpg",
  headline: hexcladFoodTourConfig.tourTitle,
  venue: "Seven iconic New York restaurants",
  dateRange: hexcladFoodTourConfig.timingLabel,
  eventDate: "January 11–15 & 20–21, 2027",
  reportingPeriod: "Partnership concept",
  campaignSummary: hexcladFoodTourConfig.positioning,
  heroImage: hexcladFoodTourConfig.images.hero,
  heroImageAlt: "ComedyUO Food Tour night with Chris Redd",
  heroCtaLabel: "See the tour",
  heroCtaHref: "#big-idea",
  featuredMetric: {
    label: "Tour",
    value: "7 nights",
    detail: "About 850 guests · January 2027",
  },
  glanceFields: [],
  activations: [],
  activationsSection: { eyebrow: "", title: "", description: "" },
  platformPerformance: {
    eyebrow: "",
    title: "",
    description: "",
    rows: [],
    totalImpressionsLabel: "",
    totalImpressionsValue: "",
  },
  kpiSection: { eyebrow: "", title: "", description: "", groups: [] },
  gallery: { eyebrow: "", title: "", images: [] },
  assets: { eyebrow: "", title: "", description: "", reportPdf: false, items: [] },
  closing: {
    thankYouHeadline: hexcladFoodTourConfig.cta.headline,
    summary: hexcladFoodTourConfig.cta.body,
    highlightCallout: hexcladFoodTourConfig.partnershipThesis,
    contact: {
      name: hexcladFoodTourConfig.cta.name,
      role: hexcladFoodTourConfig.cta.role,
      email: hexcladFoodTourConfig.cta.email,
    },
  },
  allowedEmailDomains: ["comedyuo.com", "hexclad.com"],
  nav: [
    { id: "big-idea", label: "The big idea", href: "#big-idea" },
    { id: "two-sides", label: "Two sides", href: "#two-sides" },
    { id: "the-tour", label: "The tour", href: "#the-tour" },
    { id: "why-brand", label: "Why HexClad", href: "#why-brand" },
    { id: "how-brand", label: "How HexClad shows up", href: "#how-brand" },
    { id: "tiers", label: "Sponsorship", href: "#tiers" },
    { id: "content", label: "Content", href: "#content" },
    { id: "reach", label: "Reach", href: "#reach" },
    { id: "vision", label: "Next", href: "#vision" },
  ],
  isSamplePlaceholder: false,
  sampleDisclaimer: "",
};
